// EditClient.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Select from 'react-select';
import axios from 'axios';
import LoadingPopup from '../../SharedView/LoadingPopup';
import { PhoneInput } from 'react-international-phone';
import 'react-international-phone/style.css';
import ClientDocuments from './ClientDocuments';
import BillingEntities from './BillingEntities';
import './addClient.css';

function EditClient() {
  const { clientId } = useParams();

  const [client, setClient] = useState({
    first_name: '',
    last_name: '',
    company_name: '',
    display_name: '',
    email: '',
    contact_number: '',
    trn_number: '',
    all_properties_notes: '',
    is_vat_registered: false,
    client_type: '',
    zoho_contact_id: '',
    is_multi_billing: false,
    address_line_1: '',
    city: '',
    state: '',
    country: '',
    is_active: true,

    // ✅ Admin Allocation
    account_manager_id: null,
    account_coordinator_id: null,
  });

  const [isDirty, setIsDirty] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [isLoading, setIsLoading] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [activationMessage, setActivationMessage] = useState('');

  const [adminOptions, setAdminOptions] = useState([]);
  const [adminsLoading, setAdminsLoading] = useState(false);

  // Multi-billing flips instantly via its own PATCH (never part of the form
  // save), so staff can add billing entities right away without saving.
  const [multiBillingSaving, setMultiBillingSaving] = useState(false);
  const [multiBillingError, setMultiBillingError] = useState('');

  const navigate = useNavigate();

  const clientTypeOptions = [
    { value: 'business', label: 'Business' },
    { value: 'individual', label: 'Individual' }
  ];

  const stateOptions = [
    { value: 'Dubai', label: 'Dubai' },
    { value: 'Abu Dhabi', label: 'Abu Dhabi' },
    { value: 'Sharjah', label: 'Sharjah' },
    { value: 'Ajman', label: 'Ajman' },
    { value: 'Ras Al Khaimah', label: 'Ras Al Khaimah' },
    { value: 'Fujairah', label: 'Fujairah' },
    { value: 'Umm Al Quwain', label: 'Umm Al Quwain' }
  ];

  const countryOptions = [
    { value: 'United Arab Emirates', label: 'United Arab Emirates' }
  ];

  useEffect(() => {
    const fetchClient = async () => {
      try {
        const response = await axios.get(`/api/clients/${clientId}`);
        const data = response.data;

        setClient(prev => ({
          ...prev,
          ...data,

          // ✅ Map DB fields -> frontend fields for selects
          account_manager_id: data.account_manager_id ?? data.account_manager ?? null,
          account_coordinator_id: data.account_coordinator_id ?? data.account_coordinator ?? null,

          is_active: typeof data.is_active === 'boolean' ? data.is_active : true,
          is_multi_billing: !!data.is_multi_billing,
        }));

        setLoading(false);
      } catch (err) {
        setError('Error fetching client data');
        setLoading(false);
      }
    };

    fetchClient();
  }, [clientId]);

  useEffect(() => {
    const fetchAdmins = async () => {
      setAdminsLoading(true);
      try {
        const res = await axios.get('/api/team/role', {
          params: { role: 'admin' },
          withCredentials: true,
        });

        const rows = Array.isArray(res.data) ? res.data : [];
        const opts = rows.map((u) => ({
          value: u.id,
          label: `${u.first_name || ''} ${u.last_name || ''}`.trim() + (u.username ? ` (${u.username})` : ''),
        }));

        setAdminOptions(opts);
      } catch (err) {
        console.error('Error fetching admins:', err);
        setAdminOptions([]);
      } finally {
        setAdminsLoading(false);
      }
    };

    fetchAdmins();
  }, []);

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (isDirty) {
        const confirmationMessage = 'You have unsaved changes. Are you sure you want to leave?';
        event.returnValue = confirmationMessage;
        return confirmationMessage;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  const handleChange = (e) => {
    setClient({ ...client, [e.target.name]: e.target.value });
    setIsDirty(true);
  };

  const handleSelectChange = (selectedOption, { name }) => {
    setClient({
      ...client,
      [name]: selectedOption ? selectedOption.value : null,
    });
    setIsDirty(true);
  };

  const handleToggleActive = async () => {
    const newStatus = !client.is_active;

    try {
      const response = await axios.patch(`/api/clients/${clientId}/active`, {
        is_active: newStatus,
      });

      if (response.status === 200) {
        setClient(prev => ({ ...prev, is_active: newStatus }));
        setActivationMessage(newStatus ? 'Client successfully re-activated.' : 'Client successfully deactivated.');
        setTimeout(() => setActivationMessage(''), 6000);
      } else {
        setActivationMessage('Error updating client status.');
      }
    } catch (error) {
      console.error('Error toggling client active status:', error);
      // Show backend refusals verbatim — e.g. the 409 when unarchiving a
      // client whose Zoho customer is now another client's billing entity
      // (the message names exactly which client/label holds it).
      setActivationMessage(error.response?.data?.error || 'Error updating client status.');
    }
  };

  // Multi-billing flips through its own endpoint the moment the switch is
  // toggled — deliberately NOT part of the form save, so the entities editor
  // becomes usable immediately and a later Save can never fight the toggle.
  const handleMultiBillingToggle = async (e) => {
    const enabled = e.target.checked;
    setMultiBillingSaving(true);
    setMultiBillingError('');
    try {
      const response = await axios.patch(`/api/clients/${clientId}/multi-billing`, { enabled });
      setClient((prev) => ({ ...prev, is_multi_billing: !!response.data?.is_multi_billing }));
    } catch (error) {
      // 400 = turning off while billing entities still exist. The switch is
      // controlled by client.is_multi_billing, which we never changed — so it
      // visually reverts on its own; just surface the reason.
      console.error('Error toggling multi-billing:', error);
      setMultiBillingError(error.response?.data?.error || 'Failed to update multi-billing.');
    } finally {
      setMultiBillingSaving(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // is_multi_billing is managed by its own instant PATCH — keep it out of
      // the form payload entirely (the PUT leaves absent values unchanged).
      const { is_multi_billing, ...clientPayload } = client;
      const response = await axios.put(`/api/clients/${clientId}`, {
        ...clientPayload,
        email: (client.email || '').trim().toLowerCase(),
      });

      if (response.status === 200) {
        setIsDirty(false);
        setFeedbackMessage('Client updated successfully!');
        setTimeout(() => {
          navigate(`/clients/${clientId}`);
        }, 2000);
      } else {
        setFeedbackMessage('Error saving client data.');
      }
    } catch (error) {
      const serverError = error.response?.data?.error || error.response?.data?.message;
      setFeedbackMessage(serverError || 'Error saving client.');
    } finally {
      setIsLoading(false);
    }
  };

  if (loading) return <div className="client-form-loading">Loading...</div>;
  if (error) return <div className="error-message">{error}</div>;

  return (
    <div className="client-form-page">
      <div className="client-form-header">
        <div>
          <h1 className="client-form-title">Edit Client</h1>
          <div className="client-form-subtitle">Update details, billing info and admin allocation.</div>
        </div>

        <div className="client-active-card">
          <div className="client-active-top">
            <div className="client-active-label">Status</div>
            <div className={`client-active-pill ${client.is_active ? 'active' : 'inactive'}`}>
              {client.is_active ? 'Active' : 'Inactive'}
            </div>
          </div>

          <label className="client-form-switch">
            <input
              type="checkbox"
              checked={!!client.is_active}
              onChange={handleToggleActive}
            />
            <span className="client-form-slider" />
          </label>

          {activationMessage && (
            <div className="client-active-message">{activationMessage}</div>
          )}
        </div>
      </div>

      <form className="client-form" onSubmit={handleSave}>
        <section className="client-form-card">
          <div className="client-form-card-head">
            <h2 className="client-form-card-title">Personal Information</h2>
            <p className="client-form-card-subtitle">Primary contact details for this client.</p>
          </div>

          <div className="client-form-grid">
            <div className="client-form-group">
              <label className="client-form-label">Primary Contact First Name</label>
              <input
                className="client-form-input"
                type="text"
                name="first_name"
                value={client.first_name || ''}
                onChange={handleChange}
              />
            </div>

            <div className="client-form-group">
              <label className="client-form-label">Primary Contact Last Name</label>
              <input
                className="client-form-input"
                type="text"
                name="last_name"
                value={client.last_name || ''}
                onChange={handleChange}
              />
            </div>

            <div className="client-form-group">
              <label className="client-form-label">Company Name</label>
              <input
                className="client-form-input"
                type="text"
                name="company_name"
                value={client.company_name || ''}
                onChange={handleChange}
              />
            </div>

            <div className="client-form-group">
              <label className="client-form-label">Display Name</label>
              <input
                className="client-form-input"
                type="text"
                name="display_name"
                value={client.display_name || ''}
                onChange={handleChange}
                required
                minLength={4}
              />
            </div>

            <div className="client-form-group">
              <label className="client-form-label">Email</label>
              <input
                className="client-form-input"
                type="email"
                name="email"
                value={client.email || ''}
                onChange={handleChange}
              />
            </div>

            <div className="client-form-group">
              <label className="client-form-label">Primary Contact Number</label>
              <PhoneInput
                defaultCountry="ae"
                value={client.contact_number || ''}
                onChange={(value) => {
                  setClient((prev) => ({ ...prev, contact_number: value }));
                  setIsDirty(true);
                }}
                placeholder="Enter contact number"
                className="client-phone-wrapper"
                inputClassName="client-phone-input"
              />
            </div>

            <div className="client-form-group">
              <label className="client-form-label">WhatsApp Number or Group Name</label>
              <input
                className="client-form-input"
                type="text"
                name="primary_communication_method"
                value={client.primary_communication_method || ''}
                onChange={handleChange}
                placeholder="WhatsApp group name or phone number"
              />
            </div>

            <div className="client-form-group">
              <label className="client-form-label">Zoho Contact ID</label>
              <input
                className="client-form-input"
                type="text"
                name="zoho_contact_id"
                value={client.zoho_contact_id || ''}
                onChange={handleChange}
              />
            </div>
          </div>
        </section>

        <section className="client-form-card">
          <div className="client-form-card-head">
            <h2 className="client-form-card-title">Admin Allocation</h2>
            <p className="client-form-card-subtitle">Assign admins responsible for this client.</p>
          </div>

          <div className="client-form-grid">
            <div className="client-form-group">
              <label className="client-form-label">Account Manager</label>
              <Select
                classNamePrefix="hhc-select"
                options={adminOptions}
                name="account_manager_id"
                value={adminOptions.find((o) => o.value === client.account_manager_id) || null}
                onChange={handleSelectChange}
                placeholder={adminsLoading ? 'Loading admins...' : 'Select Account Manager'}
                isClearable
              />
            </div>

            <div className="client-form-group">
              <label className="client-form-label">Account Coordinator</label>
              <Select
                classNamePrefix="hhc-select"
                options={adminOptions}
                name="account_coordinator_id"
                value={adminOptions.find((o) => o.value === client.account_coordinator_id) || null}
                onChange={handleSelectChange}
                placeholder={adminsLoading ? 'Loading admins...' : 'Select Account Coordinator'}
                isClearable
              />
            </div>
          </div>
        </section>

        <section className="client-form-card">
          <div className="client-form-card-head">
            <h2 className="client-form-card-title">Additional Details</h2>
            <p className="client-form-card-subtitle">VAT and client type information.</p>
          </div>

          <div className="client-form-grid">
            <div className="client-form-group">
              <label className="client-form-label">Client Type</label>
              <Select
                classNamePrefix="hhc-select"
                options={clientTypeOptions}
                name="client_type"
                value={clientTypeOptions.find((option) => option.value === client.client_type) || null}
                onChange={handleSelectChange}
                placeholder="Select Client Type"
              />
            </div>

            <div className="client-form-group">
              <label className="client-form-label">VAT Registered</label>
              <div className="client-form-toggle-row">
                <label className="client-form-switch">
                  <input
                    type="checkbox"
                    name="is_vat_registered"
                    checked={!!client.is_vat_registered}
                    onChange={(e) => {
                      setClient((prev) => ({ ...prev, is_vat_registered: e.target.checked }));
                      setIsDirty(true);
                    }}
                  />
                  <span className="client-form-slider" />
                </label>
                <span className="client-form-toggle-text">
                  {client.is_vat_registered ? 'Yes' : 'No'}
                </span>
              </div>
            </div>

            <div className="client-form-group">
              <label className="client-form-label" style={{ visibility: client.is_vat_registered ? 'visible' : 'hidden' }}>
                TRN Number
              </label>
              <input
                className="client-form-input"
                type="text"
                name="trn_number"
                value={client.trn_number || ''}
                onChange={handleChange}
                style={{ visibility: client.is_vat_registered ? 'visible' : 'hidden' }}
              />
            </div>
          </div>
        </section>

        <section className="client-form-card">
          <div className="client-form-card-head">
            <h2 className="client-form-card-title">Documents</h2>
            <p className="client-form-card-subtitle">Trade license and TRN / VAT certificate. Replacing or removing a document takes effect immediately.</p>
          </div>
          <ClientDocuments clientId={clientId} manage />
        </section>

        <section className="client-form-card">
          <div className="client-form-card-head">
            <h2 className="client-form-card-title">Notes for All Properties</h2>
            <p className="client-form-card-subtitle">These notes will apply to all properties under this client.</p>
          </div>

          <div className="client-form-group">
            <label className="client-form-label">Property Notes</label>
            <textarea
              className="client-form-textarea"
              name="all_properties_notes"
              value={client.all_properties_notes || ''}
              onChange={handleChange}
              rows="5"
              placeholder="Notes applicable to all properties for this client."
            />
          </div>
        </section>

        <section className="client-form-card">
          <div className="client-form-card-head">
            <h2 className="client-form-card-title">Billing Information</h2>
            <p className="client-form-card-subtitle">Billing address used for invoices.</p>
          </div>

          <div className="client-form-grid">
            <div className="client-form-group">
              <label className="client-form-label">Country</label>
              <Select
                classNamePrefix="hhc-select"
                options={countryOptions}
                name="country"
                value={countryOptions.find((option) => option.value === client.country) || null}
                onChange={handleSelectChange}
                placeholder="Select Country"
              />
            </div>

            <div className="client-form-group">
              <label className="client-form-label">State/Emirate</label>
              <Select
                classNamePrefix="hhc-select"
                options={stateOptions}
                name="state"
                value={stateOptions.find((option) => option.value === client.state) || null}
                onChange={handleSelectChange}
                placeholder="Select State"
              />
            </div>

            <div className="client-form-group">
              <label className="client-form-label">Address Line 1</label>
              <input
                className="client-form-input"
                type="text"
                name="address_line_1"
                value={client.address_line_1 || ''}
                onChange={handleChange}
                placeholder="Office / Address"
              />
            </div>

            <div className="client-form-group">
              <label className="client-form-label">City</label>
              <input
                className="client-form-input"
                type="text"
                name="city"
                value={client.city || ''}
                onChange={handleChange}
                placeholder="City"
              />
            </div>
          </div>

          <div className="client-form-group client-form-multibilling">
            <label className="client-form-label">Multi-billing</label>
            <div className="client-form-toggle-row">
              <label className="client-form-switch">
                <input
                  type="checkbox"
                  checked={!!client.is_multi_billing}
                  onChange={handleMultiBillingToggle}
                  disabled={multiBillingSaving}
                />
                <span className="client-form-slider" />
              </label>
              <span className="client-form-toggle-text">
                {multiBillingSaving ? 'Saving...' : (client.is_multi_billing ? 'On' : 'Off')}
              </span>
            </div>
            <p className="client-form-card-subtitle client-form-multibilling-hint">
              Applies immediately — invoices are raised per billing entity, and each property chooses who it bills to.
            </p>
            {multiBillingError && (
              <div className="client-form-inline-error">{multiBillingError}</div>
            )}
          </div>
        </section>

        {client.is_multi_billing && (
          <section className="client-form-card">
            <div className="client-form-card-head">
              <h2 className="client-form-card-title">Billing Entities</h2>
              <p className="client-form-card-subtitle">
                Invoices are raised per entity — properties choose who they bill to.
              </p>
            </div>
            <BillingEntities clientId={clientId} />
          </section>
        )}

        <div className="client-form-actions">
          <button type="submit" className="client-form-primary-button" disabled={isLoading}>
            {isLoading ? 'Saving...' : 'Save'}
          </button>
        </div>
      </form>

      <LoadingPopup
        isLoading={isLoading}
        feedbackMessage={feedbackMessage}
        onClose={() => setFeedbackMessage('')}
      />
    </div>
  );
}

export default EditClient;
